// timelineCalculator.ts
export interface TimelineState {
  isPast: boolean;
  isFuture: boolean;
  showTimelineBar: boolean;
  timelineBarPosition: number;
}

export interface TimelineCalculatorProps {
  currentDate: Date;
  presentationDate: string; // Format: "YYYY-MM-DD" or Date string
  presentationDay: number;
  startHour: number;
  endHour: number;
  containerHeight: number;
}

export const calculateTimelineState = ({
  currentDate,
  presentationDate,
  presentationDay,
  startHour,
  endHour,
  containerHeight
}: TimelineCalculatorProps): TimelineState => {
  // Parse presentation date
  const presentationDateObj = new Date(presentationDate);
  const today = new Date(currentDate);
  
  // Reset time to compare only dates
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const presentationDateOnly = new Date(presentationDateObj.getFullYear(), presentationDateObj.getMonth(), presentationDateObj.getDate());
  
  // Check if the presentation day is in the past, present, or future
  const dayComparison = presentationDateOnly.getTime() - todayDate.getTime();
  
  // If the day is in the past, everything is past
  if (dayComparison < 0) {
    return {
      isPast: true,
      isFuture: false,
      showTimelineBar: false,
      timelineBarPosition: 0
    };
  }
  
  // If the day is in the future, everything is future
  if (dayComparison > 0) {
    return {
      isPast: false,
      isFuture: true,
      showTimelineBar: false,
      timelineBarPosition: 0
    };
  }
  
  // If it's today, calculate the current time position
  const currentHour = today.getHours();
  const currentMinutes = today.getMinutes();
  const currentTimeInHours = currentHour + (currentMinutes / 60);
  
  // Calculate time range for the timeline
  const totalHours = endHour - startHour + 1;
  const hourHeight = containerHeight / totalHours;
  
  // Calculate the position of the current time within the timeline
  let timelineBarPosition = 0;
  let showTimelineBar = false;
  
  // Check if current time is within the timeline range
  if (currentTimeInHours >= startHour && currentTimeInHours <= endHour) {
    const timeFromStart = currentTimeInHours - startHour;
    timelineBarPosition = (timeFromStart / totalHours) * containerHeight;
    showTimelineBar = true;
  }
  
  // Determine if we're in past or future based on current time
  const isPast = currentTimeInHours < startHour || (currentTimeInHours >= startHour && currentTimeInHours <= endHour);
  const isFuture = currentTimeInHours > endHour;
  
  return {
    isPast: currentTimeInHours < startHour ? false : true,
    isFuture: currentTimeInHours > endHour ? true : false,
    showTimelineBar,
    timelineBarPosition
  };
};

export const getTimelineSegmentColor = (
  index: number,
  currentTimeInHours: number,
  startHour: number,
  presentationDate: string,
  currentDate: Date
): string => {
  const presentationDateObj = new Date(presentationDate);
  const today = new Date(currentDate);
  
  // Reset time to compare only dates
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const presentationDateOnly = new Date(presentationDateObj.getFullYear(), presentationDateObj.getMonth(), presentationDateObj.getDate());
  
  // If the presentation day is completely in the past
  if (presentationDateOnly.getTime() < todayDate.getTime()) {
    return '#32363F'; // Past color
  }
  
  // If the presentation day is completely in the future
  if (presentationDateOnly.getTime() > todayDate.getTime()) {
    return '#4B4F5A'; // Future color
  }
  
  // If it's today, check the time
  const hourForThisSegment = startHour + index;
  
  if (hourForThisSegment < currentTimeInHours) {
    return '#32363F'; // Past color
  } else {
    return '#4B4F5A'; // Future color
  }
};

// Helper function to check if a specific time slot is in the past
export const isTimeSlotPast = (
  timeSlot: string,
  presentationDate: string,
  currentDate: Date
): boolean => {
  const presentationDateObj = new Date(presentationDate);
  const today = new Date(currentDate);
  
  // Extract hour from time slot (e.g., "12h" -> 12)
  const hour = parseInt(timeSlot.replace('h', ''));
  
  // Create a specific datetime for this time slot
  const timeSlotDate = new Date(presentationDateObj);
  timeSlotDate.setHours(hour, 0, 0, 0);
  
  return timeSlotDate.getTime() < today.getTime();
};

// Helper function to check if today is the same as presentation date
export const isToday = (presentationDate: string, currentDate: Date): boolean => {
  const presentationDateObj = new Date(presentationDate);
  const today = new Date(currentDate);
  
  return presentationDateObj.toDateString() === today.toDateString();
};